import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  DATAFLOW_NAV_SECTIONS,
  MILKYWAY_NAV_SECTIONS,
  NAV_SECTIONS,
} from '../constants/navigation'
import { useAppMode } from '../contexts/AppModeContext'
import { useActiveApp } from '../contexts/ActiveAppContext'
import { navPathById } from '../constants/appSuites'
import { resolveActiveApp } from '../utils/appSuites/resolveActiveApp'
import { useFeatureFlags } from '../contexts/FeatureFlagsContext'
import { useAssumedRole } from '../contexts/AssumedRoleContext'
import { useAuthValue } from '../contexts/AuthContext'
import { useCanAccess } from './useCanAccess'
import { useNavVisibility } from './useNavVisibility'
import { isAdminPanelTabId, isAdminPanelTabVisible } from '../utils/adminPanelAccess'
import {
  buildGlobalSearchNavItems,
  type GlobalSearchNavItem,
} from '../utils/globalSearchNav'

export function useGlobalSearchNav(): GlobalSearchNavItem[] {
  const { t } = useTranslation()
  const { isMilkyway, isDataflow, isApps } = useAppMode()
  const { apps, activeApp } = useActiveApp()
  const { isItemVisible } = useNavVisibility()
  const { canViewUI, isPlatformAdmin } = useCanAccess()
  const { assumedRole } = useAssumedRole()
  const { currentUser } = useAuthValue()
  const { isEnabled } = useFeatureFlags()

  return useMemo(() => {
    const items = buildGlobalSearchNavItems({
      sections: isMilkyway
        ? MILKYWAY_NAV_SECTIONS
        : isDataflow
          ? DATAFLOW_NAV_SECTIONS
          : NAV_SECTIONS,
      translateLabel: (labelKey) => t(labelKey),
      isItemVisible,
      canViewUI,
      isFlagEnabled: isEnabled,
      isPlatformAdmin,
      canSeeAdminTab: (tabId) =>
        !isAdminPanelTabId(tabId) ||
        isAdminPanelTabVisible(
          assumedRole ? [assumedRole] : currentUser?.roles,
          tabId,
          isPlatformAdmin,
        ),
    })
    if (!isApps) return items
    const pathById = navPathById()
    const accessibleIds = apps.map((app) => app.id)
    return items.map((item) => {
      const appId = resolveActiveApp(
        item.path,
        apps,
        activeApp?.id ?? null,
        null,
        accessibleIds,
        pathById,
      )
      const owner = apps.find((app) => app.id === appId)
      return owner ? { ...item, group: owner.name } : item
    })
  }, [
    isMilkyway,
    isDataflow,
    isApps,
    apps,
    activeApp,
    t,
    isItemVisible,
    canViewUI,
    isEnabled,
    isPlatformAdmin,
    assumedRole,
    currentUser?.roles,
  ])
}
